<template>
  <div class="lab-wrapper" @mousemove="handleMouseMove" :class="activeTheme">
    <div class="lab-bg-grid"></div>
    <div class="lab-scanline"></div>
    <div class="vignette-overlay"></div>

    <header class="lab-hero">
      <div class="parallax-container">
        <div class="core-orb orb-primary" :style="parallaxStyle(60)"></div>
        <div class="core-orb orb-secondary" :style="parallaxStyle(-40)"></div>

        <div class="floating-data-glass v-reveal" :style="parallaxStyle(-15)">
          <div class="data-header">SYSTEM_LOG // {{ currentTime }}</div>
          <div class="data-row"><span>STATUS:</span> <span class="t-pulse">NODE_READY</span></div>
          <div class="data-row"><span>THREAT_LEVEL:</span> <span class="t-dim">0.0001%</span></div>
          <div class="data-row"><span>UPTIME:</span> <span class="t-white">999.98h</span></div>
        </div>

        <div class="hero-content" :style="parallaxStyle(15)">
          <div class="tech-badge-glitch">LAIQIU DIGITAL LABORATORY // SINCE 2026</div>
          <h1 class="main-title">
            <span class="glitch-text" data-text="理想">理想</span><br>
            <span class="gradient-text">编译为现实</span>
          </h1>
          <p class="hero-desc">
            来秋实验室专注于交互、算法与美学的研究实践。用温和的技术，创造安静、有价值、可沉淀的数字产品。
          </p>
          <div class="hero-actions">
            <button class="primary-btn">初始化链接</button>
            <div class="connection-status">
              <span class="dot"></span> 延迟: 12ms
            </div>
          </div>
        </div>
      </div>
    </header>

    <main class="lab-main">
      <section id="incubator" class="lab-section">
        <div class="section-label">01 / INCUBATOR</div>
        <div class="section-title-box">
          <h2>项目孵化器</h2>
          <div class="title-line"></div>
        </div>

        <div class="project-grid">
          <div class="project-card" v-for="project in projects" :key="project.id" @mouseenter="playCardGlow">
            <div class="card-noise"></div>
            <div class="p-header">
              <span class="p-status"><span class="pulse-dot"></span> {{ project.status }}</span>
              <span class="p-id">{{ project.id }}</span>
            </div>
            <h3>{{ project.name }}</h3>
            <p>{{ project.desc }}</p>

            <div class="p-progress-container">
              <div class="progress-info">
                <span>DEPLOYMENT PROGRESS</span>
                <span>{{ project.progress }}%</span>
              </div>
              <div class="progress-track">
                <div class="progress-bar" :style="{ width: project.progress + '%' }">
                  <div class="bar-glow"></div>
                </div>
              </div>
            </div>

            <div class="p-footer">
              <div class="stack-icons">
                <span v-for="tech in project.stack" :key="tech">{{ tech }}</span>
              </div>
              <div class="arrow-btn">↗</div>
            </div>
          </div>
        </div>
      </section>

      <section id="models" class="lab-section">
        <div class="section-label">02 / CORE ENGINES</div>
        <h2 class="section-title-minimal">核心能力矩阵</h2>

        <div class="dashboard-container">
          <div class="db-header">
            <div>CORE_MODEL_ID</div>
            <div>FUNCTIONAL_TYPE</div>
            <div>LATENCY</div>
            <div>TRAFFIC_LOAD</div>
            <div>AVAILABILITY</div>
          </div>
          <div class="db-row" v-for="model in aiModels" :key="model.id">
            <div class="c-id"><code>{{ model.id }}</code></div>
            <div class="c-cap">{{ model.capability }}</div>
            <div class="c-lat">{{ model.latency }}</div>
            <div class="c-load">
              <div class="load-bar">
                <div class="load-fill" :style="{ width: Math.random() * 60 + 20 + '%' }"></div>
              </div>
            </div>
            <div class="c-state">
              <span :class="['state-tag', model.stateClass]">{{ model.stateText }}</span>
            </div>
          </div>
        </div>
      </section>

      <section id="opensource" class="lab-section">
        <div class="section-label">03 / OPEN ARCHIVE</div>
        <h2 class="section-title-minimal">代码开源版图</h2>
        <div class="os-matrix">
          <div class="os-box" v-for="repo in osRepos" :key="repo.name">
            <div class="os-info">
              <div class="os-repo-name">
                <span class="git-icon"></span> {{ repo.name }}
              </div>
              <div class="os-stars">★ {{ repo.stars }}</div>
            </div>
            <p class="os-desc">{{ repo.desc }}</p>
            <div class="activity-chart">
              <div v-for="n in 24" :key="n" class="chart-bar"
                :style="{ height: Math.random() * 100 + '%', opacity: 0.2 + (n / 24) }">
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <footer class="terminal-end">
      <div class="t-wrap">
        <p>> ALL SYSTEMS OPERATIONAL</p>
        <p>> WELCOME TO THE FUTURE OF LAIQIU</p>
        <p class="t-cursor">_</p>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted } from 'vue'

const mouseX = ref(0)
const mouseY = ref(0)
const currentTime = ref('')
const activeTheme = ref('theme-1')

const handleMouseMove = (e) => {
  mouseX.value = (e.clientX / window.innerWidth - 0.5) * 2
  mouseY.value = (e.clientY / window.innerHeight - 0.5) * 2
}

const parallaxStyle = (intensity) => ({
  transform: `translate(${mouseX.value * intensity}px, ${mouseY.value * intensity}px)`,
  transition: 'transform 0.1s ease-out'
})

onMounted(() => {
  setInterval(() => {
    const now = new Date()
    currentTime.value = now.toTimeString().split(' ')[0]
  }, 1000)

  // ========== 实验室互动玩法 ==========
  const wrapper = document.querySelector('.lab-wrapper')
  const scanline = document.querySelector('.lab-scanline')

  // 点击粒子
  document.addEventListener('click', (e) => {
    const p = document.createElement('div')
    p.className = 'particle'
    p.style.left = e.clientX + 'px'
    p.style.top = e.clientY + 'px'
    wrapper.appendChild(p)
    setTimeout(() => p.remove(), 600)
  })

  // Enter 扫描
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      scanline.classList.add('scan-animate')
      setTimeout(() => scanline.classList.remove('scan-animate'), 2000)
    }
    if (e.key === '1') activeTheme.value = 'theme-1'
    if (e.key === '2') activeTheme.value = 'theme-2'
    if (e.key === '3') activeTheme.value = 'theme-3'
  })

  // 卡片电流
  window.playCardGlow = () => {
    wrapper.classList.add('card-glow-active')
    setTimeout(() => wrapper.classList.remove('card-glow-active'), 400)
  }
})

const projects = reactive([
  { id: 'LAB-01', name: '智能交互组件系统', desc: '稳定、优雅、易用的前端组件库，提供一致的体验与美感。', status: 'DEVELOPING', progress: 85, stack: ['Vue3', 'TS'] },
  { id: 'LAB-02', name: '社区数据服务中心', desc: '安全、高效的用户与内容管理系统，支撑社区稳定运行。', status: 'BUILDING', progress: 52, stack: ['Node', 'Go'] },
  { id: 'LAB-03', name: '轻量渲染引擎', desc: '高性能界面渲染库，专注流畅度与视觉质感。', status: 'PLANNING', progress: 18, stack: ['WebGL', 'TS'] }
])

const aiModels = reactive([
  { id: 'LQ-Vision-X', capability: '内容智能解析', latency: '24ms', stateClass: 'state-live', stateText: 'ACTIVE' },
  { id: 'LQ-Core-S2', capability: '上下文智能处理', latency: '12ms', stateClass: 'state-live', stateText: 'ACTIVE' },
  { id: 'LQ-Engine-T', capability: '扩展计算引擎', latency: '--', stateClass: 'state-off', stateText: 'STANDBY' }
])

const osRepos = reactive([
  { name: 'laiqiu-core', desc: '社区底层核心架构', stars: '1.4k' },
  { name: 'ui-kit', desc: '轻量级高质量组件库', stars: '0.9k' },
  { name: 'service-sdk', desc: '后端服务工具集', stars: '0.3k' }
])
</script>

<style scoped lang="scss">
.lab-wrapper {
  --main: #6d5dfc;
  --neon: #00ffe0;
  --hot: #ff5e62;
  background-color: #000;
  color: #fff;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  font-family: 'Inter', 'Space Mono', monospace;
  transition: 0.3s;
}

/* 主题 */
.lab-wrapper.theme-1 {
  --c: var(--main);
}

.lab-wrapper.theme-2 {
  --c: var(--neon);
}

.lab-wrapper.theme-3 {
  --c: var(--hot);
}

/* 粒子特效 */
.particle {
  position: fixed;
  z-index: 9999;
  width: 5px;
  height: 5px;
  background: var(--c);
  border-radius: 50%;
  pointer-events: none;
  animation: particle 0.6s forwards;
}

@keyframes particle {
  to {
    transform: translate(0, -20px) scale(0);
    opacity: 0;
  }
}

/* 扫描线 */
.lab-scanline {
  position: fixed;
  inset: 0;
  background: linear-gradient(to bottom, transparent 50%, rgba(0, 0, 0, 0.4) 51%);
  background-size: 100% 4px;
  z-index: 10;
  pointer-events: none;
  opacity: 0.15;
}

.scan-animate {
  animation: scan 2s linear;
}

@keyframes scan {
  0% {
    background-position: 0 0;
  }

  100% {
    background-position: 0 100vh;
  }
}

/* 卡片电流 */
.card-glow-active .project-card {
  animation: card-glitch 0.4s;
}

@keyframes card-glitch {
  25% {
    border-color: var(--c);
  }

  50% {
    background: rgba(var(--c), 0.05);
  }
}

.lab-bg-grid {
  position: fixed;
  inset: 0;
  background-image: linear-gradient(rgba(var(--c), 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(var(--c), 0.05) 1px, transparent 1px);
  background-size: 60px 60px;
  z-index: 0;
  mask-image: radial-gradient(circle at center, black, transparent 80%);
}

.vignette-overlay {
  position: fixed;
  inset: 0;
  background: radial-gradient(circle at center, transparent 20%, #000 100%);
  z-index: 5;
  pointer-events: none;
}

.lab-hero {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 6;
}

.core-orb {
  position: absolute;
  border-radius: 50%;
  opacity: 0.35;
  filter: blur(110px);
}

.orb-primary {
  width: 550px;
  height: 550px;
  background: var(--c);
  top: -100px;
  right: -50px;
}

.orb-secondary {
  width: 450px;
  height: 450px;
  background: var(--c);
  bottom: -100px;
  left: -50px;
  opacity: 0.2;
}

.floating-data-glass {
  position: absolute;
  top: 20%;
  left: 5%;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 24px;
  border-radius: 4px;
  font-size: 12px;
  color: #888;
  z-index: 2;

  .data-header {
    color: var(--c);
    margin-bottom: 15px;
    letter-spacing: 2px;
  }

  .data-row {
    margin-bottom: 8px;

    span {
      margin-right: 10px;
    }
  }

  .t-pulse {
    color: #00ff9d;
    animation: pulse 2s infinite;
  }

  .t-white {
    color: #fff;
  }
}

.hero-content {
  text-align: center;
  z-index: 2;
}

.main-title {
  font-size: clamp(3rem, 10vw, 7rem);
  font-weight: 900;
  line-height: 1;
  letter-spacing: -3px;
}

.gradient-text {
  background: linear-gradient(180deg, #fff 30%, var(--c) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-desc {
  max-width: 600px;
  margin: 20px auto;
  opacity: 0.6;
  line-height: 1.7;
}

.primary-btn {
  background: var(--c);
  color: #fff;
  border: none;
  padding: 14px 30px;
  border-radius: 2px;
  font-weight: bold;
  cursor: pointer;
}

.connection-status {
  display: inline-block;
  margin-left: 20px;
  opacity: 0.6;
  font-size: 12px;
}

.dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  background: #00ff9d;
  border-radius: 50%;
  margin-right: 6px;
}

.lab-main {
  position: relative;
  z-index: 20;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
}

.section-label {
  font-family: monospace;
  color: var(--c);
  letter-spacing: 4px;
  margin-bottom: 20px;
}

.section-title-box {
  margin-bottom: 60px;

  h2 {
    font-size: 42px;
    margin-bottom: 10px;
  }

  .title-line {
    width: 60px;
    height: 4px;
    background: var(--c);
  }
}

.project-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-bottom: 150px;
}

.project-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 40px;
  transition: 0.4s;

  &:hover {
    transform: translateY(-8px);
    border-color: rgba(var(--c), 0.3);
    background: rgba(var(--c), 0.02);
  }
}

.p-progress-container {
  margin: 30px 0;

  .progress-info {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: #555;
    margin-bottom: 10px;
  }

  .progress-track {
    height: 2px;
    background: rgba(255, 255, 255, 0.05);
  }

  .progress-bar {
    height: 100%;
    background: var(--c);
    position: relative;

    .bar-glow {
      position: absolute;
      right: 0;
      top: -2px;
      width: 4px;
      height: 6px;
      background: #fff;
      box-shadow: 0 0 10px var(--c);
    }
  }
}

.dashboard-container {
  background: #050505;
  border: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom: 150px;

  .db-header {
    display: grid;
    grid-template-columns: 2fr 2fr 1fr 2fr 1fr;
    padding: 20px 40px;
    background: rgba(255, 255, 255, 0.02);
    font-size: 11px;
    color: #444;
    letter-spacing: 2px;
  }

  .db-row {
    display: grid;
    grid-template-columns: 2fr 2fr 1fr 2fr 1fr;
    padding: 30px 40px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
    align-items: center;
  }
}

.load-bar {
  width: 100px;
  height: 3px;
  background: #111;
  border-radius: 100px;
  overflow: hidden;
}

.load-fill {
  height: 100%;
  background: var(--c);
}

.state-tag {
  font-size: 10px;
  padding: 4px 12px;
  border-radius: 100px;

  &.state-live {
    border: 1px solid #00ff9d;
    color: #00ff9d;
  }

  &.state-off {
    border: 1px solid #333;
    color: #333;
  }
}

.os-matrix {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 100px;
}

.os-box {
  background: rgba(255, 255, 255, 0.02);
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.terminal-end {
  padding: 100px 5%;
  border-top: 1px solid rgba(255, 255, 255, 0.05);

  .t-wrap {
    color: #00ff9d;
    font-size: 13px;
    opacity: 0.5;
  }

  .t-cursor {
    animation: blink 1s infinite;
  }
}

@keyframes blink {
  50% {
    opacity: 0;
  }
}

@keyframes pulse {
  50% {
    opacity: 0.5;
  }
}

@media (max-width: 1024px) {

  .project-grid,
  .os-matrix {
    grid-template-columns: 1fr;
  }

  .db-header {
    display: none;
  }

  .db-row {
    grid-template-columns: 1fr;
    gap: 15px;
    text-align: center;
  }
}
</style>